import React,{useEffect} from "react";
import {
  Avatar,
  Box,
  Chip,
  LinearProgress,
  Paper,
  Typography,
} from "@mui/material";

import Inventory2Icon from "@mui/icons-material/Inventory2";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import PaymentsIcon from "@mui/icons-material/Payments";
import ShoppingBagIcon from "@mui/icons-material/ShoppingBag";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

import {
  Area,
  AreaChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import "./Dashboard.css";

const cards = [
  {
    title: "Total Orders",
    value: "12,840",
    growth: "+18%",
    helper: "1,240 new orders",
    icon: <ShoppingBagIcon />,
    tone: "blue",
  },
  {
    title: "Products",
    value: "1,240",
    growth: "+9%",
    helper: "86 active categories",
    icon: <Inventory2Icon />,
    tone: "green",
  },
  {
    title: "Revenue",
    value: "Rs 84,500",
    growth: "+22%",
    helper: "Best month so far",
    icon: <PaymentsIcon />,
    tone: "amber",
  },
  {
    title: "Delivered",
    value: "8,420",
    growth: "+14%",
    helper: "92% fulfillment rate",
    icon: <LocalShippingIcon />,
    tone: "violet",
  },
];

const salesData = [
  { name: "Jan", sales: 400, orders: 240 },
  { name: "Feb", sales: 300, orders: 198 },
  { name: "Mar", sales: 500, orders: 310 },
  { name: "Apr", sales: 700, orders: 428 },
  { name: "May", sales: 620, orders: 390 },
  { name: "Jun", sales: 820, orders: 510 },
];

const pieData = [
  { name: "Orders", value: 400 },
  { name: "Products", value: 300 },
  { name: "Revenue", value: 300 },
];

const recentOrders = [
  { id: "#TNQ-1048", customer: "Rohit Sharma", status: "Delivered", total: "Rs 8,450" },
  { id: "#TNQ-1047", customer: "Aarav Mehta", status: "Processing", total: "Rs 4,220" },
  { id: "#TNQ-1046", customer: "Priya Nair", status: "Packed", total: "Rs 12,100" },
  { id: "#TNQ-1045", customer: "Kiran Das", status: "Pending", total: "Rs 2,860" },
];

const inventoryAlerts = [
  { item: "Vacuum Pump", stock: 18, level: 72 },
  { item: "Copper Tubes", stock: 9, level: 36 },
  { item: "Leak Detector", stock: 6, level: 24 },
];

const COLORS = ["#2563eb", "#16a34a", "#f59e0b"];

const statusClass = {
  Delivered: "success",
  Processing: "info",
  Packed: "warning",
  Pending: "danger",
};

const Dashboard = () => {
  useEffect(() => {

    document.title =
      "Admin Dashboard | Tecniqa";

  }, []);
  return (
    <Box className="admin-dashboard">
      <section className="dashboard-hero">
        <div>
          <Typography className="dashboard-eyebrow">Admin Overview</Typography>
          <Typography component="h1" className="dashboard-title">
            Welcome back, Admin
          </Typography>
          <Typography className="dashboard-subtitle">
            Track sales, monitor orders, and manage store health from one clean workspace.
          </Typography>
        </div>

        <div className="dashboard-hero-actions">
          <Chip className="dashboard-hero-chip" label="Live Store" />
          <Chip
            className="dashboard-hero-chip strong"
            icon={<TrendingUpIcon />}
            label="22% growth"
          />
        </div>
      </section>

      <section className="dashboard-kpi-grid">
        {cards.map((item) => (
          <Paper key={item.title} className={`dashboard-kpi-card ${item.tone}`}>
            <div className="kpi-card-top">
              <Avatar className="kpi-icon">{item.icon}</Avatar>
              <Chip className="kpi-chip" label={item.growth} size="small" />
            </div>

            <Typography className="kpi-label">{item.title}</Typography>
            <Typography className="kpi-value">{item.value}</Typography>
            <Typography className="kpi-helper">{item.helper}</Typography>
          </Paper>
        ))}
      </section>

      <section className="dashboard-main-grid">
        <Paper className="dashboard-panel sales-panel">
          <div className="panel-head">
            <div>
              <Typography className="panel-eyebrow">Performance</Typography>
              <Typography component="h2" className="panel-title">
                Sales Overview
              </Typography>
            </div>
            <Chip className="panel-chip" label="Last 6 months" />
          </div>

          <div className="chart-wrap">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={salesData} margin={{ top: 10, right: 12, left: -18, bottom: 0 }}>
                <defs>
                  <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2563eb" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#2563eb" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#e5e7eb" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fill: "#64748b", fontSize: 12 }} />
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
                <Area
                  type="monotone"
                  dataKey="sales"
                  stroke="#2563eb"
                  strokeWidth={3}
                  fill="url(#salesGradient)"
                  activeDot={{ r: 6 }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Paper>

        <Paper className="dashboard-panel distribution-panel">
          <div className="panel-head compact">
            <div>
              <Typography className="panel-eyebrow">Store Mix</Typography>
              <Typography component="h2" className="panel-title">
                Distribution
              </Typography>
            </div>
          </div>

          <div className="donut-wrap">
            <ResponsiveContainer width="100%" height={235}>
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  innerRadius={62}
                  outerRadius={92}
                  paddingAngle={4}
                >
                  {pieData.map((entry, index) => (
                    <Cell key={entry.name} fill={COLORS[index]} />
                  ))}
                </Pie>
                <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid #e2e8f0" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>

          <div className="distribution-list">
            {pieData.map((item, index) => (
              <div key={item.name} className="distribution-item">
                <span style={{ backgroundColor: COLORS[index] }} />
                <p>{item.name}</p>
                <strong>{item.value}</strong>
              </div>
            ))}
          </div>
        </Paper>
      </section>

      <section className="dashboard-bottom-grid">
        <Paper className="dashboard-panel">
          <div className="panel-head">
            <div>
              <Typography className="panel-eyebrow">Operations</Typography>
              <Typography component="h2" className="panel-title">
                Recent Orders
              </Typography>
            </div>
            <Chip className="panel-chip" label="Today" />
          </div>

          <div className="orders-list">
            {recentOrders.map((order) => (
              <div key={order.id} className="order-row">
                <div>
                  <Typography className="order-id">{order.id}</Typography>
                  <Typography className="order-customer">{order.customer}</Typography>
                </div>
                <Chip
                  className={`order-status ${statusClass[order.status]}`}
                  label={order.status}
                  size="small"
                />
                <Typography className="order-total">{order.total}</Typography>
              </div>
            ))}
          </div>
        </Paper>

        <Paper className="dashboard-panel">
          <div className="panel-head">
            <div>
              <Typography className="panel-eyebrow">Inventory</Typography>
              <Typography component="h2" className="panel-title">
                Stock Watch
              </Typography>
            </div>
            <WarningAmberIcon className="panel-warning-icon" />
          </div>

          <div className="stock-list">
            {inventoryAlerts.map((item) => (
              <div key={item.item} className="stock-item">
                <div className="stock-meta">
                  <Typography>{item.item}</Typography>
                  <span>{item.stock} units left</span>
                </div>
                <LinearProgress
                  variant="determinate"
                  value={item.level}
                  className={item.level < 35 ? "stock-progress low" : "stock-progress"}
                />
              </div>
            ))}
          </div>
        </Paper>
      </section>
    </Box>
  );
};

export default Dashboard;
